import styled from "styled-components";

export const Wrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.gray[100],
  flexGrow: 1,
  paddingBottom: 50,
  minHeight: "calc(100vh - 200px)",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  overflowX: "hidden",

  [theme.down("sm")]: {
    paddingBottom: 30,
  },
}));

export const TitleComponent = styled("div")(({ theme }) => ({
  ...theme.paddings(20, 0, 10, 0),
  fontSize: 32,
  fontWeight: 600,
  color: theme.palette.text.primary,

  [theme.down("sm")]: {
    fontSize: 24,
    ...theme.paddings(16, 0, 8, 0),
  },
}));

export const Subtitle = styled("div")(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
  marginBottom: 24,
  fontWeight: 400,

  [theme.down("sm")]: {
    fontSize: 14,
    marginBottom: 16,
  },
}));

export const FiltersCard = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.white,
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  border: `1px solid ${theme.palette.border.main}`,

  [theme.down("sm")]: {
    padding: 16,
    marginBottom: 16,
  },
}));

export const FiltersHeader = styled("div")(({ theme, $clickable }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: $clickable ? "pointer" : "default",

  [theme.up("sm")]: {
    marginBottom: 20,
    borderBottom: `1px solid ${theme.palette.border.main}`,
    paddingBottom: 12,
  },
}));

export const FiltersTitle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 18,
  fontWeight: 600,
  color: theme.palette.text.primary,

  [theme.down("sm")]: {
    fontSize: 16,
  },
}));

export const FiltersToggleIcon = styled("div")(({ theme, $open }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
  transition: "transform 0.3s ease",
  transform: $open ? "rotate(180deg)" : "rotate(0deg)",
}));

export const FiltersContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 20,

  [theme.down("sm")]: {
    gap: 16,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.border.main}`,
    marginTop: 16,
  },
}));

export const FilterRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: 16,

  [theme.down("lg")]: {
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },

  [theme.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },
}));

export const FilterButtonsRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,

  [theme.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,

    "& > div": {
      display: "flex",
      gap: 12,
      width: "100%",

      "& > button": {
        flex: 1,
      },
    },
  },
}));

export const FilterGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

export const FilterLabel = styled("label")(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.primary,

  [theme.down("sm")]: {
    fontSize: 13,
  },
}));

export const FilterInputWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
}));

export const FilterInput = styled("input")(({ theme, $hasIcon }) => ({
  width: "100%",
  padding: $hasIcon ? "10px 12px 10px 40px" : "10px 12px",
  border: `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.white,
  transition: "all 0.2s ease",
  boxSizing: "border-box",

  "&:focus": {
    outline: "none",
    borderColor: theme.palette.secondary.main,
    boxShadow: `0 0 0 3px ${theme.palette.secondary.focusShadow}`,
  },

  "&::placeholder": {
    color: theme.palette.text.muted,
  },
}));

export const FilterSelect = styled("select")(({ theme }) => ({
  width: "100%",
  padding: "10px 40px 10px 12px",
  border: `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.white,
  appearance: "none",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxSizing: "border-box",

  "&:focus": {
    outline: "none",
    borderColor: theme.palette.secondary.main,
    boxShadow: `0 0 0 3px ${theme.palette.secondary.focusShadow}`,
  },
}));

export const FilterDateInput = styled("input")(({ theme }) => ({
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.white,
  transition: "all 0.2s ease",
  cursor: "pointer",
  boxSizing: "border-box",

  "&:focus": {
    outline: "none",
    borderColor: theme.palette.secondary.main,
    boxShadow: `0 0 0 3px ${theme.palette.secondary.focusShadow}`,
  },

  "&::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    color: theme.palette.text.muted,
  },
}));

export const ApplyButton = styled("button")(({ theme }) => ({
  padding: "10px 24px",
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.white,
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.secondary.dark,
  },

  "&:active": {
    transform: "scale(0.98)",
  },

  [theme.down("sm")]: {
    padding: "12px 16px",
  },
}));

export const ResetButton = styled("button")(({ theme }) => ({
  padding: "10px 24px",
  backgroundColor: theme.palette.white,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.gray[100],
    borderColor: theme.palette.border.dark,
  },

  "&:active": {
    transform: "scale(0.98)",
  },

  [theme.down("sm")]: {
    padding: "12px 16px",
  },
}));

export const ExportButton = styled("button")(({ theme }) => ({
  padding: "10px 16px",
  backgroundColor: theme.palette.white,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: theme.palette.gray[100],
    borderColor: theme.palette.border.dark,
  },

  "&:active": {
    transform: "scale(0.98)",
  },

  [theme.down("sm")]: {
    padding: "12px 16px",
    width: "100%",
  },
}));

export const NotFoundComponent = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  minHeight: 128,
  textAlign: "center",
  fontSize: 20,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.white,
  borderRadius: 8,
  border: `1px solid ${theme.palette.border.main}`,

  [theme.down("sm")]: {
    fontSize: 16,
    minHeight: 100,
  },
}));

export const PaginationWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 20px",
  gap: 16,
  flexWrap: "wrap",
  borderTop: `1px solid ${theme.palette.border.main}`,
  backgroundColor: theme.palette.white,

  [theme.down("sm")]: {
    flexDirection: "column",
    gap: 12,
    alignItems: "center",
    padding: "12px 16px",
  },
}));

export const PaginationInfo = styled("div")(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,

  [theme.down("sm")]: {
    fontSize: 13,
    textAlign: "center",
  },
}));

export const PaginationButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  justifyContent: "center",
}));

export const PaginationPageButton = styled("button")(({ theme, $active }) => ({
  ...theme.mixins.flexCenterCenter,
  padding: "8px 12px",
  backgroundColor: $active ? theme.palette.secondary.main : theme.palette.white,
  color: $active ? theme.palette.white : theme.palette.text.primary,
  border: $active ? `1px solid ${theme.palette.secondary.main}` : `1px solid ${theme.palette.border.medium}`,
  borderRadius: 6,
  fontSize: 14,
  fontWeight: $active ? 600 : 400,
  cursor: "pointer",
  minWidth: $active ? 40 : "auto",
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  transition: "all 0.2s ease",
  whiteSpace: "nowrap",

  "&:hover:not(:disabled)": {
    backgroundColor: $active ? theme.palette.secondary.dark : theme.palette.gray[100],
    borderColor: $active ? theme.palette.secondary.dark : theme.palette.border.dark,
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  "&:active:not(:disabled)": {
    transform: "scale(0.98)",
  },

  [theme.down("sm")]: {
    padding: "6px 10px",
    fontSize: 13,
    height: 32,
  },
}));

export const StatusBadgeWrapper = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 14px",
  backgroundColor: "#d1fae5",
  borderRadius: "20px",
  color: "#10b981",
  fontSize: 13,
  fontWeight: 500,
  whiteSpace: "nowrap",
  lineHeight: 1,

  "& svg": {
    color: "#10b981",
    flexShrink: 0,
  },

  [theme.down("sm")]: {
    padding: "4px 10px",
    fontSize: 12,
  },
}));

export const TransactionsTableWrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.white,
  borderRadius: 8,
  border: `1px solid ${theme.palette.border.main}`,
  boxShadow: `0 1px 3px ${theme.palette.shadow.medium}`,
  overflow: "hidden",
  marginBottom: 24,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
}));

// Desktop table - hidden on mobile
export const DesktopTableWrapper = styled("div")(({ theme }) => ({
  display: "block",

  [theme.down("sm")]: {
    display: "none",
  },
}));

// Mobile cards - hidden on desktop
export const MobileCardsWrapper = styled("div")(({ theme }) => ({
  display: "none",

  [theme.down("sm")]: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
}));

export const TransactionCard = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.white,
  borderRadius: 8,
  border: `1px solid ${theme.palette.border.main}`,
  padding: 16,
  boxShadow: `0 1px 3px ${theme.palette.shadow.medium}`,
}));

export const CardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 12,
}));

export const CardMerchant = styled("div")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 4,
}));

export const CardDateTime = styled("div")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
}));

export const CardAmount = styled("div")(({ theme }) => ({
  fontSize: 20,
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 12,
}));

export const CardDetails = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  paddingTop: 12,
  borderTop: `1px solid ${theme.palette.border.main}`,
}));

export const CardDetailRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
}));

export const CardDetailLabel = styled("span")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

export const CardDetailValue = styled("span")(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.primary,
  textAlign: "right",
  wordBreak: "break-word",

  "& a": {
    color: theme.palette.secondary.main,
    textDecoration: "none",

    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export const FiltersToggleButton = styled("button")(({ theme }) => ({
  display: "none",

  [theme.down("sm")]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: theme.palette.text.secondary,
  },
}));
